// React
import { useMemo, useCallback } from "react";
// Helpers
import { reduce } from "@mefisto/utils";
// Framework
import { getData, useApolloClient, useMutation } from "model/core";
import { usePortal } from "stack";
import { useResources } from "resource";

export const useEntityCreate = (
  Entity,
  {
    input,
    resources: customResources,
    languages,
    files,
    silent,
    fetchPolicy,
    nextFetchPolicy,
    tags,
  } = {}
) => {
  // Framework
  const { uploadQueue: queue } = usePortal();
  const client = useApolloClient();
  // Resources
  const { resources: defaultResources } = useResources();
  const resources = useMemo(() => {
    return customResources ?? defaultResources;
  }, [customResources, defaultResources]);
  // Model
  const Tags = useMemo(() => {
    const { CREATE, UPDATE } = tags ?? Entity.Tags;
    if (!CREATE || !UPDATE) {
      throw TypeError("[CREATE, UPDATE] tags must be defined");
    }
    return { CREATE, UPDATE };
  }, [Entity, tags]);
  const entity = useMemo(() => {
    return new Entity(client, { queue });
  }, [Entity, queue, client]);
  // Callbacks
  const getFiles = useCallback((files) => {
    return reduce(
      files,
      (result, value, key) => {
        result[key] = {
          name: value.name,
          size: value.size,
          contentType: value.type,
        };
        return result;
      },
      {}
    );
  }, []);
  const getVariables = useCallback(
    ({ input, resources, languages, files } = {}) => ({
      ...((input || files) && {
        input: { ...input, ...getFiles(files) },
      }),
      ...(resources && { resources }),
      ...(languages && { languages }),
    }),
    [getFiles]
  );
  // Mutation
  const [create, mutation] = useMutation(Tags.CREATE, {
    fetchPolicy,
    nextFetchPolicy,
    notifyOnNetworkStatusChange: true,
  });
  const { loading, called, error } = mutation;
  // Memo
  const data = useMemo(() => {
    return getData(mutation);
  }, [mutation]);
  // Callbacks
  const performCreate = useCallback(
    async (options = {}) => {
      const variables = getVariables({
        input: options.input ?? input,
        resources: options.resources ?? resources,
        languages: options.languages ?? languages,
        files: options.files ?? files,
      });
      try {
        const result = await create({
          variables,
          update: (cache, { data }) => {
            entity.onCreate({
              ...variables,
              files: options.files ?? files,
              data,
              silent,
            });
          },
        });
        return getData(result);
      } catch (error) {
        if (options.throwOnError) {
          throw error;
        }
      }
    },
    [getVariables, input, resources, languages, files, silent, create, entity]
  );
  // Render
  return {
    called,
    data,
    error,
    loading,
    create: performCreate,
  };
};
